import React, {useEffect, useState} from "react";
import ViewVoucherDialogComponent from "./viewVoucherDialog.component";
import {useDispatch, useSelector} from "react-redux";
import {hideBackdrop, showBackdrop} from "../../../../backdrop";
import {uploadFileToS3} from "../../../../../app/feature";
import {UploadType} from "../../../../../lib/constants/aws_s3";
import {unwrapResult} from "@reduxjs/toolkit";
import {AccessRights} from "../../../../../lib/constants/accessRights";
import {canAccess} from "../../../../profile/redux/selector/profile.selector";
import surveyApi from "../../../../../app/api/survey";
import {getApiLang, getLang} from "../../../../../app/feature/constants";
import {addAlert} from "../../../../notification";
import XLSX from "xlsx";

const ViewVoucherDialogContainer = ({
  selectedSurvey,
  isOpen,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const [isAdding, setIsAdding] = useState(false);

  // CSV upload states
  const [file, setFile] = useState(null);
  const [isValidFile, setIsValidFile] = useState(false);
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [tableHeader, setTableHeader] = useState([]);
  const [tableValues, setTableValues] = useState([]);
  const [errorField, setErrorField] = useState(null);
  const [errorTitles, setErrorTitles] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lang = useSelector((state) => state.constant.languages);
  const timezone = useSelector((state) => state.profile.timezone);
  const canEditVoucher = useSelector(state => canAccess(state, AccessRights.Survey.VOUCHER_WRITE_UPDATE));

  const [setting, setSetting] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = async () => {
    setIsFetching(true);
    const { success, data, code } = await surveyApi.fetchVoucherSetting({ uuid: selectedSurvey })
      .catch((error) => error);

    if (success) {
      setSetting(data);
    } else if (!success && code === "error.ERROR_SURVEY_VOUCHER_SETTING_NOT_FOUND") {
      setSetting({ enabled: false, distribution_method: "1" });
    } else {
      dispatch(
        addAlert({ severity: "error", message: getApiLang(lang, code) })
      );
    }

    setIsFetching(false);
  }

  // Handle CSV file change
  const handleFileChange = (selectedFile) => {
    if (!selectedFile) {
      resetFileStates();
      return;
    }

    setIsFileProcessing(true);
    setErrorField(null);
    setErrorTitles([]);
    setShowPreview(false);

    setIsValidFile(true);
    setIsFileProcessing(false);
  };

  // Reset file states
  const resetFileStates = () => {
    setFile(null);
    setIsValidFile(false);
    setTableHeader([]);
    setTableValues([]);
    setErrorField(null);
    setErrorTitles([]);
    setShowPreview(false);
    setIsFileProcessing(false);
  };

  // Handle download template
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Add "voucher_code" in A1
    const ws = XLSX.utils.aoa_to_sheet([["voucher_code"]]);

    // Append worksheet
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    // Trigger download
    XLSX.writeFile(wb, "voucher_template.xlsx");
  };

  // Handle import submit
  const handleImportSubmit = async () => {
    console.log(file, isValidFile)
    if (!file || !isValidFile) return;
    dispatch(showBackdrop());
    setIsSubmitting(true);

    dispatch(uploadFileToS3({
      uploadType: UploadType.surveyVoucherImport,
      file,
      id: selectedSurvey,
      fileName: `${Math.random().toString(36).substr(2, 6)}-${Date.now()}-${file.name}`,
    })).then(unwrapResult).then(async (url) => {
      const { success, code } = await surveyApi.appendVoucherListing({ uuid: selectedSurvey, url })
          .catch((error) => error);

      if (success) {
        setIsAdding(false);
        resetFileStates();
      } else {
        dispatch(
          addAlert({ severity: "error", message: getApiLang(lang, code) })
        );
      }

      setIsSubmitting(false);
      dispatch(hideBackdrop());
    });
  };

  // Handle validate
  const handleValidate = () => {
    if (!file) return;

    setIsValidating(true);
    handleFileChange(file);
    setTimeout(() => setIsValidating(false), 1000);
  };

  useEffect(() => {
    if (selectedSurvey === null) return;

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSurvey]);

  const onSubmit = async (value) => {
    dispatch(showBackdrop());

    if (value.emailImage !== null && value.emailImage !== setting?.email_image) {
      if (value.emailImage.size / 1024 / 1024 > 10) {
        dispatch(
          addAlert({ severity: "error", message: getLang(lang, "error.ERROR_FILE_EXCEED_LIMIT") })
        );
        dispatch(hideBackdrop());
        return;
      }

      dispatch(uploadFileToS3({
        uploadType: UploadType.surveyVoucherEmailImage,
        file: value.emailImage,
        id: selectedSurvey,
        fileName: `${Math.random().toString(36).substr(2, 6)}-${Date.now()}-${value.emailImage.name}`,
      })).then(unwrapResult).then(async (url) => {
        const { success, code } = await surveyApi.updateVoucherSetting({
          uuid: selectedSurvey,
          enable: value.enabled,
          method: value.method,
          emailTitle: value.emailTitle,
          emailContent: value.emailContent,
          emailImage: url,
        }).catch((error) => error);

        dispatch(
          addAlert({ severity: success ? "success" : "error", message: getApiLang(lang, code) })
        );

        dispatch(hideBackdrop());
      });
    } else {
      const { success, code } = await surveyApi.updateVoucherSetting({
        uuid: selectedSurvey,
        enable: value.enabled,
        method: value.method,
        emailTitle: value.emailTitle,
        emailContent: value.emailContent,
        emailImage: value.emailImage ?? "",
      }).catch((error) => error);

      dispatch(
        addAlert({ severity: success ? "success" : "error", message: getApiLang(lang, code) })
      );

      dispatch(hideBackdrop());
    }
  }

  const onFileExceed = () => {
    dispatch(
      addAlert({ severity: "error", message: getLang(lang, "message.error.ERROR_FILE_EXCEED_LIMIT") })
    );
  }

  const handleCloseDialog = () => {
    handleClose();
    setIsAdding(false);
  }

  return (
    <ViewVoucherDialogComponent
      isOpen={isOpen}
      handleClose={handleCloseDialog}
      timezone={timezone}
      lang={lang}
      isAdding={isAdding}
      setIsAdding={setIsAdding}
      setting={setting}
      isFetching={isFetching}
      handleSubmit={onSubmit}

      // CSV upload props
      file={file}
      setFile={setFile}
      handleFileChange={handleFileChange}
      tableHeader={tableHeader}
      tableValues={tableValues}
      errorField={errorField}
      errorTitles={errorTitles}
      showPreview={showPreview}
      isValidFile={isValidFile}
      handleValidate={handleValidate}
      handleImportSubmit={handleImportSubmit}
      isValidating={isValidating}
      isSubmitting={isSubmitting}
      isFileProcessing={isFileProcessing}
      handleDownloadTemplate={handleDownloadTemplate}
      canEditVoucher={canEditVoucher}
      selectedSurvey={selectedSurvey}
      onFileExceed={onFileExceed}
    />
  );
};

export default ViewVoucherDialogContainer;
